import type React from "react";
import { FormattedHTMLMessage } from "react-intl";

import { type VerificationService, type ThirdPartyLoopResponse } from "../../lib/types/types";
import { ThirdPartyLoopProvider, VerificationStepsEnum } from "../../lib/types/runtimeTypes";
import {
  ButtonType,
  SingleSignOnComponent,
  type SingleSignOnMessages,
} from "../SingleSignOnComponent";
import { LogoComponent } from "../LogoComponent/LogoComponent";
import { AnnouncingHeader } from "../AnnouncingHeader/AnnouncingHeader";
import { useFlag } from "../FeatureFlags/hooks";

interface StepThirdPartyLoopProps {
  verificationService: VerificationService;
}

export const thirdPartyLoopMessageMap: {
  [key in ThirdPartyLoopProvider]: SingleSignOnMessages;
} = {
  [ThirdPartyLoopProvider.SIGNZY]: {
    title: (
      <FormattedHTMLMessage
        id="step.thirdPartyLoop.signzy.title"
        tagName="h1"
        defaultMessage="Verify using DigiLocker"
      />
    ),
    subtitle: (
      <FormattedHTMLMessage
        id="step.thirdPartyLoop.signzy.subtitle"
        defaultMessage="Verify using your DigiLocker login information"
      />
    ),
    login: (
      <FormattedHTMLMessage
        id="step.thirdPartyLoop.signzy.login"
        defaultMessage="Sign in to DigiLocker"
      />
    ),
    cancel: (
      <FormattedHTMLMessage
        id="step.thirdPartyLoop.signzy.cancel"
        defaultMessage="Upload Proof of Identity"
      />
    ),
  },
};

interface ThirdPartyLoopErrorProps {
  logo: React.ReactNode;
  /**
   * True when this error view is the whole step. StepPending also renders it as a
   * fragment inside its own main landmark, and only the step root may own one.
   */
  isStepRoot?: boolean;
}

export const ThirdPartyLoopError = ({ logo, isStepRoot = false }: ThirdPartyLoopErrorProps) => {
  const errorContent = (
    <div className="sid-header sid-l-horz-center">
      <div className="sid-error-logo-container sid-l-horz-center">{logo}</div>
      <AnnouncingHeader align="center">
        <FormattedHTMLMessage id="error" tagName="h1" />
      </AnnouncingHeader>
      <div className="sid-header__subtitle sid-l-horz-center sid-l-space-top-md">
        <div className="sid-error-msg sid-l-horz-center">
          <FormattedHTMLMessage
            id="step.thirdPartyLoop.error"
            defaultMessage="Provider not supported"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div
      id="sid-step-third-party-loop"
      className="sid-step-third-party-loop-container sid-l-container"
      data-testid="sid-step-third-party-loop-error"
    >
      {isStepRoot ? <div role="main">{errorContent}</div> : errorContent}
    </div>
  );
};

const StepThirdPartyLoopComponent = ({ verificationService }: StepThirdPartyLoopProps) => {
  const verificationResponse = verificationService.verificationResponse as ThirdPartyLoopResponse;
  const messages =
    thirdPartyLoopMessageMap[verificationResponse.thirdPartyLoopProvider] || undefined;
  const shouldHideIcon = useFlag("hc-my-sheer-id-theme-builder");

  if (!messages) {
    return (
      <ThirdPartyLoopError
        isStepRoot
        logo={
          <LogoComponent
            verificationService={verificationService}
            fallbackLogo={
              !shouldHideIcon && (
                <img
                  className="sid-error-logo sid-l-lead-image"
                  alt=""
                  src="https://assets-resources.sheerid.com/common/images/2018/icons/circle-exclamation.svg"
                />
              )
            }
          />
        }
      />
    );
  }

  return (
    <SingleSignOnComponent
      verificationService={verificationService}
      stepName={VerificationStepsEnum.thirdPartyLoop}
      messages={messages}
      cancelButtonType={ButtonType.SECONDARY}
    />
  );
};

export { StepThirdPartyLoopComponent };
